import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { MatCheckbox } from '@angular/material/checkbox';
import { LabelsApi } from '@core/api/labels/labels.api';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Label } from '@interfaces';
import { QuotationService } from 'app/main/quotations/services/quotation.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'label-selector',
  templateUrl: './label-selector.component.html',
  styleUrls: ['./label-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations
})
export class LabelSelectorComponent implements OnInit, OnDestroy {
  @Input() public type: string;
  @Input() public quotationId: string;
  @Input() public maintenanceId: string;
  @Input() public associatedLabels: Label[] = [];

  @Output() public wasUpdatedLabels = new EventEmitter<Boolean>();

  public labels: Label[] = [];
  private currentOffset = 0;
  private hasMoreLabels = true;
  private unsubscribeAll = new Subject<any>();

  constructor(
    private labelsApi: LabelsApi,
    private maintenancesApi: MaintenancesApi,
    private quotationApi: QuotationService,
    private progressBar: FuseProgressBarService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public ngOnInit(): void {
    this.fetchLabels();
  }

  private fetchLabels(): void {
    try {
      this.labelsApi
        .getLabels({ type: this.type }, this.currentOffset)
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe((result) => {
          const newLabels = result.items;

          if (newLabels.length > 0) {
            this.labels = this.labels.concat(newLabels);

            this.currentOffset += 50;
            this.fetchLabels();
          } else {
            this.hasMoreLabels = false;
          }
        });
    } catch (err) {
      sentry.captureException(err);
    }
  }

  public isSelectedLabel(labelId: string): Boolean {
    return !!this.associatedLabels.find((label) => label && label.id === labelId);
  }

  public async addRemoveLabel(checkbox: MatCheckbox, label: Label): Promise<void> {
    if (checkbox.checked) {
      this.associatedLabels.push(label);
    } else {
      const labelIndex = this.associatedLabels.findIndex((item) => item.id === label.id);
      this.associatedLabels.splice(labelIndex, 1);
    }

    await this.saveLabel(label, !checkbox.checked);
    this.wasUpdatedLabels.emit(true);
  }

  private async saveLabel(label: Label, isRemove: boolean): Promise<void> {
    this.progressBar.show();

    const input: any = {
      labelId: label.id
    };

    if (this.type === 'maintenance') {
      input.maintenanceId = this.maintenanceId;
      await this.maintenancesApi.saveMaintenanceLabel(input, isRemove);
    } else {
      input.quotationId = this.quotationId;
      await this.quotationApi.saveQuotationLabels(input, isRemove);
    }

    this.progressBar.hide();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }
}
